import { db, COLLECTIONS } from '../firebase/db';
import { doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { DeviceSettings } from './types';

// Get device settings
export async function getDeviceSettings(
  familyId: string,
  deviceId: string
): Promise<DeviceSettings | null> {
  try {
    const settingsRef = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'settings', 'prefs');
    const snapshot = await getDoc(settingsRef);

    if (!snapshot.exists()) {
      return null;
    }

    const data = snapshot.data();
    return {
      id: deviceId,
      displayName: data.displayName || 'Device',
      restrictionMode: data.restrictionMode || 'BLOCKLIST',
      newAppPolicy: data.newAppPolicy || 'ALLOW',
      newAppDefaultLimitMinutes: data.newAppDefaultLimitMinutes || 0,
      timezone: data.timezone || 'America/New_York',
      notificationsEnabled: data.notificationsEnabled !== false,
      notificationTypes: data.notificationTypes || {
        blockEvents: true,
        limitReached: true,
        appInstalled: false,
        scheduleChange: false,
      },
      dataExportEnabled: data.dataExportEnabled !== false,
      createdAt: data.createdAt ? new Date(data.createdAt) : undefined,
      updatedAt: data.updatedAt ? new Date(data.updatedAt) : undefined,
    };
  } catch (err) {
    console.error('Failed to load device settings:', err);
    return null;
  }
}

// Update device settings
export async function updateDeviceSettings(
  familyId: string,
  deviceId: string,
  settings: Partial<DeviceSettings>
): Promise<void> {
  try {
    const settingsRef = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'settings', 'prefs');

    const updateData: any = {
      ...settings,
      updatedAt: Timestamp.now(),
    };

    // Remove id field if present
    delete updateData.id;
    delete updateData.createdAt;

    await setDoc(settingsRef, updateData, { merge: true });
  } catch (err) {
    throw new Error(`Failed to update settings: ${(err as Error).message}`);
  }
}

// Bulk update multiple setting fields
export async function bulkUpdateSettings(
  familyId: string,
  deviceId: string,
  updates: Record<string, any>
): Promise<void> {
  try {
    const settingsRef = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'settings', 'prefs');

    const updateData = {
      ...updates,
      updatedAt: Timestamp.now(),
    };

    await updateDoc(settingsRef, updateData);
  } catch (err) {
    throw new Error(`Failed to update settings: ${(err as Error).message}`);
  }
}

// Reset settings to defaults
export async function resetDeviceSettings(
  familyId: string,
  deviceId: string
): Promise<void> {
  try {
    const settingsRef = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'settings', 'prefs');

    const defaults = {
      displayName: 'Device',
      restrictionMode: 'BLOCKLIST',
      newAppPolicy: 'ALLOW',
      newAppDefaultLimitMinutes: 0,
      timezone: 'America/New_York',
      notificationsEnabled: true,
      notificationTypes: {
        blockEvents: true,
        limitReached: true,
        appInstalled: false,
        scheduleChange: false,
      },
      dataExportEnabled: true,
      updatedAt: Timestamp.now(),
    };

    await setDoc(settingsRef, defaults, { merge: true });
  } catch (err) {
    throw new Error(`Failed to reset settings: ${(err as Error).message}`);
  }
}
